import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SchemaData {
  '@context': string;
  '@type'?: string;  // Made optional to support @graph format
  [key: string]: any;
}

interface SchemaInjectorProps {
  schemas: SchemaData[];
  title?: string;
  description?: string;
  canonical?: string;
  keywords?: string;
  ogImage?: string;
}

/**
 * SchemaInjector - Reusable component for injecting structured data and meta tags
 * 
 * Features:
 * - Dynamic schema markup injection
 * - Meta tag management with react-helmet-async
 * - SEO optimization for Colorado First Plumbing
 * - Support for multiple schema types per page
 */
export const SchemaInjector: React.FC<SchemaInjectorProps> = ({
  schemas,
  title,
  description,
  canonical,
  keywords,
  ogImage = "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Prices-you-can-see-Reviews-you-can-trust.png"
}) => {
  const baseUrl = "https://colorado-plumbing.com";

  return (
    <Helmet>
      {/* Page Title */}
      {title && <title>{title}</title>}
      
      {/* Meta Description */}
      {description && <meta name="description" content={description} />}
      
      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph Tags */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      
      {/* Structured Data Schemas */}
      {schemas.map((schema, index) => (
        <script 
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema, null, 2)
          }}
        />
      ))}
    </Helmet>
  );
};

// Common schema creators for Colorado First Plumbing
export const createLocalBusinessSchema = (location: string, coords?: { lat: string; lng: string }) => ({
  '@context': 'https://schema.org',
  '@type': 'Plumber',
  '@id': 'https://colorado-plumbing.com/',
  'name': 'Colorado First Plumbing and Sewer LLC',
  'description': `Professional plumbing services in ${location}, Colorado. Water heater installation from $2,000, sewer scope $175, complete plumbing services. Licensed master plumber owned with 360 Google reviews.`,
  'url': `https://colorado-plumbing.com/${location.toLowerCase().replace(/ /g, '-')}-plumber`,
  'email': 'contact@bestmasterplumber.com',
  'logo': 'https://colorado-plumbing.com/colorado-plumbing-uploads/brand/colorado-first-plumbing-logo-white-background.png',
  'image': 'https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Prices-you-can-see-Reviews-you-can-trust.png',
  'address': {
    '@type': 'PostalAddress',
    'streetAddress': '5108 Sydney Ave',
    'addressLocality': 'Littleton',
    'addressRegion': 'CO',
    'postalCode': '80130',
    'addressCountry': 'US'
  },
  'geo': coords ? {
    '@type': 'GeoCoordinates',
    'latitude': coords.lat,
    'longitude': coords.lng
  } : undefined,
  'areaServed': {
    '@type': 'City',
    'name': location,
    'addressRegion': 'CO',
    'addressCountry': 'US'
  },
  'parentOrganization': {
    '@id': 'https://colorado-plumbing.com/#organization'
  },
  'openingHours': [
    'Mo-Sa 08:00-17:00'
  ],
  'priceRange': '$$',
  'currenciesAccepted': 'USD',
  'paymentAccepted': 'Cash, Check, Credit Card, Financing Available',
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '4.8',
    'reviewCount': '360'
  },
  'hasOfferCatalog': {
    '@type': 'OfferCatalog',
    'name': `${location} Plumbing Services with Transparent Pricing`,
    'itemListElement': [
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Water Heater Installation',
          'description': `Professional water heater installation in ${location} starting at $2,000`,
          'areaServed': location
        },
        'price': '2000',
        'priceCurrency': 'USD'
      },
      {
        '@type': 'Offer', 
        'itemOffered': {
          '@type': 'Service',
          'name': 'Sewer Camera Inspection',
          'description': `Professional sewer camera scope inspection in ${location}`,
          'areaServed': location
        },
        'price': '175',
        'priceCurrency': 'USD'
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Sewer Line Replacement',
          'description': `Complete sewer line replacement services in ${location}`,
          'areaServed': location
        },
        'price': '12000',
        'priceCurrency': 'USD'
      }
    ]
  }
});

export const createServiceSchema = (serviceName: string, description: string, price: string | number) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  'name': serviceName,
  'description': description,
  'provider': {
    '@id': 'https://colorado-plumbing.com/'
  },
  'areaServed': {
    '@type': 'State',
    'name': 'Colorado'
  },
  'offers': {
    '@type': 'Offer',
    'price': price.toString(),
    'priceCurrency': 'USD',
    'availability': 'https://schema.org/InStock',
    'validFrom': '2025-01-01',
    'description': 'Transparent pricing with no hidden fees'
  }
});

export const createFAQSchema = (faqItems: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqItems.map(item => ({
    '@type': 'Question',
    'name': item.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': item.answer
    }
  }))
});

export const createBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': crumb.name,
    'item': crumb.url
  }))
});